import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { ArrowLeft, MapPin, Navigation, Share2, Phone } from 'lucide-react';
import { SEO } from '../components/common/SEO';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { categories, getOfferType } from '../data/constants';

const API = process.env.REACT_APP_BACKEND_URL || '';

export const DealDetailPage = () => {
  const { id } = useParams();
  const [deal, setDeal] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => { fetchDeal(); }, [id]);

  const fetchDeal = async () => {
    try { const res = await axios.get(`${API}/api/deals/${id}`); setDeal(res.data); }
    catch (e) { toast.error('Deal not found'); navigate('/deals'); }
    setLoading(false);
  };

  const handleNavigate = () => {
    if (deal.maps_link) window.open(deal.maps_link, '_blank');
    else if (deal.latitude && deal.longitude) window.open(`https://www.google.com/maps/dir/?api=1&destination=${deal.latitude},${deal.longitude}`, '_blank');
  };

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) { try { await navigator.share({ title: deal.title, text: deal.description, url }); } catch (e) {} }
    else { navigator.clipboard.writeText(url); toast.success('Link copied!'); }
  };

  if (loading) return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><div className="w-8 h-8 border-4 border-slate-700 border-t-transparent rounded-full animate-spin"></div></div>;
  if (!deal) return null;

  const category = categories.find(c => c.id === deal.category) || categories[7];
  const offerType = getOfferType(deal.offer_type);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="deal-detail-page">
      <SEO title={`${deal.title} - JustOffs`} description={deal.description} />
      <Header />
      <div className="max-w-3xl mx-auto px-4 py-6">
        <Link to="/deals" className="inline-flex items-center gap-2 text-slate-600 hover:text-orange-500 mb-4 text-sm" data-testid="back-link"><ArrowLeft className="w-4 h-4" /> Back</Link>
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          <div className="h-56 md:h-72 bg-slate-100 relative">
            <img src={deal.image_url || category.placeholder} alt={deal.title} className="w-full h-full object-cover" onError={(e) => { e.target.src = category.placeholder; }} />
            <span className={`absolute top-3 left-3 ${offerType.color} text-sm px-3 py-1.5 rounded-full border font-medium flex items-center gap-1.5`}>
              {offerType.emoji} {offerType.name}
            </span>
          </div>
          <div className="p-6">
            <div className="flex items-start justify-between gap-4 mb-3">
              <h1 className="text-xl md:text-2xl font-bold text-slate-800" data-testid="deal-title">{deal.title}</h1>
              {deal.price && <span className="text-xl md:text-2xl font-bold text-slate-800" data-testid="deal-price">₹{deal.price}</span>}
            </div>
            <div className="flex flex-wrap gap-2 mb-4">
              <span className={`flex items-center gap-1 ${category.color} text-white px-3 py-1 rounded-full text-sm`}><category.icon className="w-4 h-4" /> {category.name}</span>
              <span className="flex items-center gap-1 bg-slate-100 px-3 py-1 rounded-full text-sm"><MapPin className="w-4 h-4" /> {deal.area}</span>
            </div>
            <p className="text-slate-600 mb-6 whitespace-pre-line" data-testid="deal-description">{deal.description}</p>
            {deal.phone && <a href={`tel:${deal.phone}`} className="flex items-center gap-2 text-slate-600 mb-4 hover:text-orange-500 text-sm" data-testid="deal-phone"><Phone className="w-4 h-4" /> {deal.phone}</a>}
            <div className="flex gap-3">
              <button onClick={handleNavigate} className="flex-1 bg-orange-500 text-white py-3 rounded-xl font-semibold hover:bg-orange-600 transition flex items-center justify-center gap-2" data-testid="navigate-btn"><Navigation className="w-5 h-5" /> Navigate</button>
              <button onClick={handleShare} className="px-5 py-3 border border-slate-200 rounded-xl hover:bg-slate-50 transition" data-testid="share-btn"><Share2 className="w-5 h-5 text-slate-600" /></button>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};
